/**
 * Formats a number into a human-readable string with appropriate units (thousand, million, billion, trillion)
 * and dynamic precision based on the magnitude of the number.
 * 
 * @param value - The number to format
 * @returns A formatted string with units and appropriate decimal places
 */
export const formatReadableNumber = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const units: Array<{ threshold: number; label: string }> = [
    { threshold: 1_000_000_000_000, label: 'trillion' },
    { threshold: 1_000_000_000, label: 'billion' },
    { threshold: 1_000_000, label: 'million' },
    { threshold: 1_000, label: 'thousand' }
  ];

  for (const { threshold, label } of units) {
    if (abs >= threshold) {
      const scaled = abs / threshold;
      // Dynamic precision: larger numbers get fewer decimals
      const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
      const rounded = Number(scaled.toFixed(decimals)).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals
      });
      return `${sign}${rounded} ${label}`;
    }
  }

  // For small values, keep comma separators with up to 2 decimals
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

/**
 * Formats a number in a compact financial format (e.g., 50k, 1.2M, $50,000)
 * 
 * @param value - The number to format
 * @param options - Formatting options
 * @returns A formatted string in compact format
 */
export const formatFinancialNumber = (value: number, options?: { useCurrency?: boolean; compact?: boolean }): string => {
  const { useCurrency = false, compact = true } = options || {};
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const prefix = useCurrency ? '$' : '';

  if (compact) {
    // Compact format: 50k, 1.2M, etc.
    if (abs >= 1_000_000_000) {
      const scaled = abs / 1_000_000_000;
      const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
      return `${sign}${prefix}${scaled.toFixed(decimals)}B`;
    }
    if (abs >= 1_000_000) {
      const scaled = abs / 1_000_000;
      const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
      return `${sign}${prefix}${scaled.toFixed(decimals)}M`;
    }
    if (abs >= 1_000) {
      const scaled = abs / 1_000;
      const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
      return `${sign}${prefix}${scaled.toFixed(decimals)}k`;
    }
  }

  // Full format with commas: $50,000 or 50,000
  return `${sign}${prefix}${abs.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Parses a string value to a number, returning a fallback if the value is not a valid finite number.
 * 
 * @param value - The string value to parse
 * @param fallback - The value to return if parsing fails (default: 0)
 * @returns The parsed number or the fallback value
 */
export const parseNumber = (value: string, fallback = 0): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

